import { useCallback, useEffect, useRef } from 'react';
import { type Chatbox } from 'talkjs/all';
import {
  buildDoNotTranslateSendOptions,
  type SmartPhraseKey,
} from '@rsos/utils/SmartPhrases';

export type ProtectPrefilledPhrases = (
  resolvedPhrases:
    | Partial<Record<SmartPhraseKey, string>>
    | readonly string[]
    | undefined,
) => void;

/**
 * Shields the resolved smart-phrase values of a message prefilled into the
 * TalkJS message field from translation.
 *
 * A message the frontend sends itself can carry the values as send options. A
 * message merely dropped into the message field cannot: the send belongs to the
 * TalkJS UI, so there is no sendMessage call to attach them to. This subscribes
 * to onSendMessage and tags the outgoing message there instead.
 *
 * Call `protectPrefilledPhrases` wherever the field is prefilled, passing either
 * the keyed `resolvedPhrases` record or a bare list of values.
 */
export const useDoNotTranslateMessageField = (chatbox: Chatbox | null) => {
  // A ref, so the one subscription below reads the current list without being
  // torn down and re-registered on every prefill.
  const protectedPhrasesRef = useRef<string[]>([]);

  // Own effect with an unsubscribe on cleanup: onSendMessage stacks handlers
  // rather than replacing them, so a handler left behind would tag a later,
  // unrelated send.
  useEffect(() => {
    if (!chatbox) return;

    const subscription = chatbox.onSendMessage(event => {
      const protectedPhrases = protectedPhrasesRef.current;
      if (!protectedPhrases.length) return;

      // One-shot: the prefill has left the message field either way.
      protectedPhrasesRef.current = [];

      // Only phrases the dispatcher left in the text need protecting, so an
      // edit that removed them all leaves the send untouched.
      const sendOptions = buildDoNotTranslateSendOptions(
        protectedPhrases.filter(phrase => event.message.text?.includes(phrase)),
      );

      if (sendOptions) {
        event.override({ custom: sendOptions.custom });
      }
    });

    return () => subscription.unsubscribe();
  }, [chatbox]);

  // Stable, so callers can list it in effect dependencies.
  const protectPrefilledPhrases = useCallback<ProtectPrefilledPhrases>(
    resolvedPhrases => {
      protectedPhrasesRef.current = resolvedPhrases
        ? Object.values(resolvedPhrases)
        : [];
    },
    [],
  );

  return { protectPrefilledPhrases };
};
